import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import SimpleFormModal from '../../../common/modals/SimpleFormModal';
import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';

import { RESERVED_MAPPING_KEYS } from './eclipse.constants';

function EclipseCustomMappingModal({
  visible,
  onClose,
  onSave,
  existingMappings,
  initialMapping,
}) {
  const [displayName, setDisplayName] = useState('');
  const [technicalName, setTechnicalName] = useState('');
  const [technicalNameError, setTechnicalNameError] = useState();

  const isEditing = !!initialMapping;

  const existingValues = useMemo(() => {
    const initialValue = initialMapping?.value?.toLowerCase();
    return new Set(
      existingMappings
        .filter((field) => field.value?.toLowerCase() !== initialValue)
        .map((field) => field.value?.toLowerCase()),
    );
  }, [existingMappings, initialMapping]);

  const validateTechnicalName = (value) => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    const normalized = trimmed.toLowerCase();
    if (
      RESERVED_MAPPING_KEYS.has(normalized)
      || existingValues.has(normalized)
    ) {
      return 'A mapping with this Technical Name already exists';
    }
    return undefined;
  };

  const onSaveClicked = () => {
    const label = displayName?.trim();
    const value = technicalName?.trim();
    if (!label || !value) return;
    const error = validateTechnicalName(value);
    if (error) {
      setTechnicalNameError(error);
      return;
    }
    onSave({ label, value });
  };

  useEffect(() => {
    if (!visible) {
      setDisplayName('');
      setTechnicalName('');
      setTechnicalNameError();
      return;
    }
    setDisplayName(initialMapping?.label ?? '');
    setTechnicalName(initialMapping?.value ?? '');
  }, [visible, initialMapping]);

  return (
    <SimpleFormModal
      title={isEditing ? 'Edit Custom Mapping' : 'Add Custom Mapping'}
      visible={visible}
      onClose={onClose}
      onSave={onSaveClicked}
      disabled={!displayName?.trim() || !technicalName?.trim() || !!technicalNameError}
      errorText={technicalNameError}
    >
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>Display Name</div>
          <OnTraccrTextInput
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter Display Name"
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>Technical Name</div>
          <OnTraccrTextInput
            value={technicalName}
            onChange={(e) => {
              const { value } = e.target;
              setTechnicalName(value);
              setTechnicalNameError(validateTechnicalName(value));
            }}
            placeholder="Enter Technical Name"
          />
        </div>
      </div>
    </SimpleFormModal>
  );
}

EclipseCustomMappingModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  existingMappings: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  initialMapping: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
};

EclipseCustomMappingModal.defaultProps = {
  existingMappings: [],
  initialMapping: null,
};

export default EclipseCustomMappingModal;
